import { useEffect, useRef, useState } from 'react';

export interface SwipeState {
  /** Current horizontal drag offset in pixels. */
  dx: number;
  /** True while the user is actively dragging. */
  dragging: boolean;
  /** Direction the card was thrown in. null until committed. */
  thrown: 'left' | 'right' | null;
}

interface UseSwipeOptions {
  /** Pixels past which a release commits to that direction. */
  threshold?: number;
  /** Called when the card commits in a direction. */
  onCommit?: (direction: 'left' | 'right') => void;
}

/**
 * Custom touch/mouse/keyboard swipe handler. Targets a card element
 * via the returned bind() spread. Returns the current drag offset
 * so the caller can apply transforms.
 */
export function useSwipe({ threshold = 120, onCommit }: UseSwipeOptions = {}) {
  const [state, setState] = useState<SwipeState>({ dx: 0, dragging: false, thrown: null });
  const startX = useRef<number | null>(null);
  const pointerId = useRef<number | null>(null);
  const targetRef = useRef<HTMLElement | null>(null);

  function commit(direction: 'left' | 'right') {
    setState({ dx: direction === 'left' ? -window.innerWidth : window.innerWidth, dragging: false, thrown: direction });
    onCommit?.(direction);
  }

  function reset() {
    setState({ dx: 0, dragging: false, thrown: null });
    startX.current = null;
    pointerId.current = null;
  }

  function onPointerDown(e: React.PointerEvent<HTMLElement>) {
    if (state.thrown) return;
    targetRef.current = e.currentTarget;
    pointerId.current = e.pointerId;
    startX.current = e.clientX;
    e.currentTarget.setPointerCapture(e.pointerId);
    setState({ dx: 0, dragging: true, thrown: null });
  }

  function onPointerMove(e: React.PointerEvent<HTMLElement>) {
    if (!state.dragging || startX.current === null) return;
    const dx = e.clientX - startX.current;
    setState((s) => ({ ...s, dx }));
  }

  function onPointerUp(e: React.PointerEvent<HTMLElement>) {
    if (!state.dragging) return;
    const dx = state.dx;
    try {
      if (pointerId.current !== null) e.currentTarget.releasePointerCapture(pointerId.current);
    } catch {
      // ignore; capture may already be released
    }
    if (dx > threshold) {
      commit('right');
    } else if (dx < -threshold) {
      commit('left');
    } else {
      setState({ dx: 0, dragging: false, thrown: null });
    }
  }

  function onPointerCancel(e: React.PointerEvent<HTMLElement>) {
    onPointerUp(e);
  }

  // Keyboard alternative — arrows for swipe
  useEffect(() => {
    function onKey(ev: KeyboardEvent) {
      if (state.thrown) return;
      if (ev.key === 'ArrowLeft') commit('left');
      else if (ev.key === 'ArrowRight') commit('right');
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.thrown]);

  return {
    state,
    bind: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel },
    reset,
    commit,
  };
}
